import React from 'react';
import { Link } from 'react-router-dom';
import { Users, Heart, Shield, Check, MapPin, Building2 } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import Seo from '@/components/Seo';
import Reveal from '@/components/Reveal';
import PageHero from '@/components/PageHero';

const features = [
  'Emotionally regulated environment',
  'Consistent, person-centered support',
  'Structured daily routines',
  'Respectful, patient care approach',
  'Focus on individual growth and stability'
];

const Chasnell = () => {
  return (
    <div className="relative z-10 min-h-screen">
      <Seo title="The Chasnell Home™" description="The Chasnell Home™ is the first Forever Path Living™ residence in Richmond, Virginia — our initial residential program being developed to serve adult females as part of our initial Virginia DBHDS licensure strategy. Licensure in progress." />
      <PageHero
        eyebrow="Our First Residential Home"
        title="The Chasnell Home™"
        subtitle="Richmond, Virginia — building relationships before opening our doors."
      />

      {/* Distinction note */}
      <section className="py-16 md:py-20 px-6 bg-sage-100">
        <Reveal className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-cream-100 border border-gold-500/30 rounded-full mb-6">
            <MapPin className="w-4 h-4 text-gold-700" />
            <p className="text-forest-800 font-medium text-sm">Richmond, Virginia</p>
          </div>
          <p className="text-lg md:text-xl text-forest-700 leading-relaxed">
            The Chasnell Home™ is the first residential home of Forever Path Living™. It is our initial residential
            program and is currently being developed to serve adult females as part of our initial Virginia DBHDS
            licensure strategy. This reflects our current operational plan — not the limits of our long-term vision.
          </p>
        </Reveal>
      </section>

      {/* Overview cards */}
      <section className="py-20 md:py-28 px-6 bg-cream-100">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {[
              { icon: <Users className="w-7 h-7" />, title: 'Relationship-Centered', text: 'A home built on dignity, trust, and belonging' },
              { icon: <Heart className="w-7 h-7" />, title: 'Structured Support', text: 'An emotionally regulated environment' },
              { icon: <Building2 className="w-7 h-7" />, title: 'Licensure In Progress', text: 'Welcoming referral inquiries as we complete licensure' }
            ].map((c, i) => (
              <Reveal key={c.title} delay={i * 70}>
                <Card className="h-full p-8 bg-cream-200 border border-sage-200 rounded-3xl text-center">
                  <div className="inline-flex p-4 rounded-2xl bg-sage-100 text-forest-700 mb-4">{c.icon}</div>
                  <h3 className="font-serif text-xl text-forest-800 mb-2">{c.title}</h3>
                  <p className="text-forest-700">{c.text}</p>
                </Card>
              </Reveal>
            ))}
          </div>

          <Reveal>
            <Card className="p-8 md:p-12 bg-cream-200 border border-sage-200 rounded-3xl">
              <h2 className="font-serif text-2xl md:text-3xl text-forest-800 mb-6">About The Chasnell Home™</h2>
              <p className="text-lg text-forest-700 leading-relaxed mb-6">
                This residence is designed to support individuals with intellectual and developmental disabilities,
                including those with co-occurring behavioral or mental health support needs.
              </p>
              <p className="text-lg text-forest-700 leading-relaxed mb-8">
                The focus is on creating an emotionally regulated environment where individuals are supported
                consistently and respectfully.
              </p>

              <div className="bg-cream-100 rounded-2xl p-8 border border-sage-200">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-3 rounded-2xl bg-sage-100 text-forest-700"><Shield className="w-6 h-6" /></div>
                  <h3 className="font-serif text-xl text-forest-800">Support Environment Features</h3>
                </div>
                <ul className="space-y-3">
                  {features.map((f) => (
                    <li key={f} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-gold-700 flex-shrink-0 mt-1" />
                      <span className="text-lg text-forest-700">{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Card>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-24 px-6 bg-forest-900">
        <Reveal className="max-w-3xl mx-auto text-center">
          <h2 className="font-serif text-3xl md:text-4xl text-cream-100 mb-4">Interested in Learning More?</h2>
          <p className="text-lg text-sage-100/85 mb-8 leading-relaxed">
            For inquiries about The Chasnell Home™, please begin a conversation with us. Referral inquiries are
            reviewed on a rolling basis as we complete licensure.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/placement">
              <Button data-testid="chasnell-inquiry-btn" className="rounded-full bg-cream-100 hover:bg-white text-forest-900 px-8 py-6 text-base font-semibold w-full sm:w-auto">
                Begin a Conversation
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" data-testid="chasnell-contact-btn" className="rounded-full border-2 border-cream-100 text-cream-100 hover:bg-cream-100/10 px-8 py-6 text-base w-full sm:w-auto bg-transparent">
                Contact Us
              </Button>
            </Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
};

export default Chasnell;
